import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Settings, FolderTree, Building2, Database, Ruler,
  Users, ScrollText, BookOpen, Factory, Bell, Wrench, PackageCheck, FileText, Star, DollarSign, AlertTriangle, Plug,
  Layers, Shield, ClipboardCheck,
} from 'lucide-react';

/**
 * Config-driven Admin tab navigation.
 * Each entry: { value, icon, label, testId, show, badge }
 *  - show: (ctx) => boolean    (ctx = { isAdmin, isGroupAdmin })
 *  - badge: (ctx) => number    (optional; rendered as a count badge)
 */
export function AdminTabsList({ t, isAdmin, isGroupAdmin, pendingCompanies, pendingUsers, pendingMaterials }) {
  const ctx = { isAdmin, isGroupAdmin };

  const tabs = [
    { value: 'scenarios', icon: Settings, label: t('admin.scenarios'), testId: 'admin-scenarios-tab' },
    { value: 'categories', icon: FolderTree, label: t('admin.categories'), testId: 'admin-categories-tab' },
    {
      value: 'orgs', icon: Building2, label: 'Organisation', testId: 'admin-orgs-tab',
      badge: () => pendingCompanies, badgeClass: 'bg-amber-500',
    },
    {
      value: 'users', icon: Users, label: t('admin.users'), testId: 'admin-users-tab',
      badge: () => pendingUsers, badgeClass: 'bg-orange-500',
    },
    {
      value: 'material-approvals', icon: PackageCheck, label: t('admin.materialApprovals') || 'Material-Freigabe',
      testId: 'admin-material-approvals-tab', show: (c) => c.isAdmin,
      badge: () => pendingMaterials, badgeClass: 'bg-orange-500',
    },
    { value: 'notifications', icon: Bell, label: t('admin.notifications'), testId: 'admin-notifications-tab' },
    { value: 'masterdata', icon: Database, label: t('admin.masterData') || 'Stammdaten', testId: 'admin-masterdata-tab', show: (c) => c.isAdmin || c.isGroupAdmin },
    { value: 'units', icon: Ruler, label: 'Einheiten', testId: 'admin-units-tab', show: (c) => c.isAdmin },
    { value: 'manufacturers', icon: Factory, label: 'Hersteller', testId: 'admin-manufacturers-tab', show: (c) => c.isAdmin },
    { value: 'machine-manufacturers', icon: Factory, iconClass: 'text-purple-500', label: 'Maschinen-Hersteller', testId: 'admin-machine-manufacturers-tab', show: (c) => c.isAdmin },
    { value: 'vertical-categories', icon: Layers, iconClass: 'text-blue-500', label: 'Vertical-Kategorien', testId: 'admin-vertical-categories-tab', show: (c) => c.isAdmin },
    { value: 'rating-categories', icon: Star, label: 'Bewertungen', testId: 'admin-rating-categories-tab', show: (c) => c.isAdmin },
    { value: 'fee-config', icon: DollarSign, label: t('admin.page.fees'), testId: 'admin-fee-config-tab', show: (c) => c.isAdmin },
    { value: 'disputes', icon: AlertTriangle, iconClass: 'text-amber-500', label: 'Reklamationen', testId: 'admin-disputes-tab', show: (c) => c.isAdmin },
    { value: 'bug-reports', icon: AlertTriangle, label: 'Bug Reports', testId: 'admin-bug-reports-tab', show: (c) => c.isAdmin },
    { value: 'api-integrations', icon: Plug, label: 'API & Schnittstellen', testId: 'admin-api-integrations-trigger', show: (c) => c.isAdmin },
    { value: 'audit', icon: ScrollText, label: t('admin.auditLog'), testId: 'admin-audit-tab' },
    { value: 'security', icon: Shield, label: 'Sicherheit', testId: 'admin-security-tab', show: (c) => c.isAdmin },
    { value: 'iso27001', icon: ClipboardCheck, label: 'ISO 27001', testId: 'admin-iso27001-tab', show: (c) => c.isAdmin },
    { value: 'guide', icon: BookOpen, label: t('admin.guide'), testId: 'admin-guide-tab' },
    { value: 'platform-settings', icon: Wrench, label: t('admin.platformSettings') || 'Einstellungen', testId: 'admin-settings-tab', show: (c) => c.isAdmin },
    { value: 'legal-pages', icon: FileText, label: t('admin.legalPages') || 'Rechtliches', testId: 'admin-legal-pages-tab', show: (c) => c.isAdmin },
  ];

  const visible = tabs.filter((tab) => !tab.show || tab.show(ctx));

  return (
    <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1 rounded-lg" data-testid="admin-tabs">
      {visible.map((tab) => {
        const Icon = tab.icon;
        const badgeCount = tab.badge ? tab.badge() : 0;
        return (
          <TabsTrigger key={tab.value} value={tab.value} className="gap-1.5 text-xs sm:text-sm" data-testid={tab.testId}>
            <Icon className={`h-3.5 w-3.5 ${tab.iconClass || ''}`} />
            {tab.label}
            {badgeCount > 0 && (
              <Badge className={`ml-1 h-5 min-w-[20px] px-1.5 text-xs ${tab.badgeClass || 'bg-orange-500'}`}>{badgeCount}</Badge>
            )}
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
}
